import React, { useEffect, useRef } from 'react';
import { Calendar, Heart, BookOpen, Clock, Trophy, Flame, Sparkles, Info, History, MessageCircle, Tag } from 'lucide-react';
import { RosaryStreakData, NovenaState, ActiveNovena, ChapletType, RosarySession } from '../../types';
import { calculateCompletionPercentage } from '../../utils/novenaCalculations';
import { TOTAL_DAYS } from '../../constants/novena';
import { CHAPLET_INFO } from '../../constants/chaplets';
import { NOVENA_INFO } from '../../constants/novenas';
import { TrialBanner } from '../common/TrialBanner';
import { AppFooter } from '../common/AppFooter';
import { UnifiedHeader } from '../common/UnifiedHeader';
import { OfflineGraceWarning } from '../common/OfflineGraceWarning';
import { SacredArtViewer } from '../common/SacredArtViewer';
import { MyNovenas } from '../novenas/MyNovenas';
import { RecommendationsSection } from '../recommendations/RecommendationsSection';
import { useChapletFavorites } from '../../hooks/useChapletFavorites';

interface PrayerSelectionScreenProps {
  novenaState: NovenaState;
  rosaryStreak: RosaryStreakData;
  activeNovenas: ActiveNovena[];
  onStartNovena: () => void;
  onContinueNovena: () => void;
  onPrayRosary: () => void;
  onPrayChaplet: () => void;
  onSelectChaplet?: (chapletType: ChapletType) => void;
  onContinueIndividualNovena: (novenaId: string) => void;
  onStartNewNovena: () => void;
  onRemoveNovena?: (novenaId: string) => void;
  hasAccess: boolean;
  onUpgradeClick: () => void;
  onShow54DayNovenaInfo?: () => void;
  onShowNovenaInfo?: () => void;
  onShowRosaryInfo?: () => void;
  onShowChapletInfo?: () => void;
  onShowSettings?: () => void;
  onShowHistory?: () => void;
  onOpenSearch?: () => void;
  onStartRecommendedNovena?: (novenaType: string, targetDate: string) => void;
  onStart54DayFromRecommendation?: (targetDate: string) => void;
}

const getPrayerTypeDisplayName = (session: RosarySession): string => {
  switch (session.prayerType) {
    case '54-day-novena':
      return `54-Day Novena${session.currentDay ? ` - Day ${session.currentDay}` : ''}`;
    case 'daily-rosary':
      return `Rosary: ${session.mystery} Mysteries`;
    case 'chaplet':
      const chapletName = session.chaplet && session.chaplet in CHAPLET_INFO ? CHAPLET_INFO[session.chaplet].name : 'Unknown Chaplet';
      return `Chaplet: ${chapletName}`;
    case 'novena':
      const novenaName = session.novena && session.novena in NOVENA_INFO ? NOVENA_INFO[session.novena].name : 'Unknown Novena';
      return `Novena: ${novenaName}${session.currentDay ? ` - Day ${session.currentDay}` : ''}`;
    default:
      return 'Prayer';
  }
};

export const PrayerSelectionScreen: React.FC<PrayerSelectionScreenProps> = ({
  novenaState,
  rosaryStreak,
  activeNovenas,
  onStartNovena,
  onContinueNovena,
  onPrayRosary,
  onPrayChaplet,
  onSelectChaplet,
  onContinueIndividualNovena,
  onStartNewNovena,
  onRemoveNovena,
  hasAccess,
  onUpgradeClick,
  onShow54DayNovenaInfo,
  onShowNovenaInfo,
  onShowRosaryInfo,
  onShowChapletInfo,
  onShowSettings,
  onShowHistory,
  onOpenSearch,
  onStartRecommendedNovena,
  onStart54DayFromRecommendation
}) => {
  const { currentDay, completedDays, startDate } = novenaState;
  const completionPercentage = calculateCompletionPercentage(completedDays, TOTAL_DAYS);
  const { displayFavorites } = useChapletFavorites();

  // Track if paywall has been shown to prevent infinite loop
  const hasShownPaywallRef = useRef(false);

  const isNovenaActive = !!startDate;
  const isNovenaComplete = completedDays.size === TOTAL_DAYS;

  // Auto-show paywall modal when user has no access (only once)
  useEffect(() => {
    if (!hasAccess && !hasShownPaywallRef.current) {
      hasShownPaywallRef.current = true;
      // Small delay to ensure the screen has rendered first
      const timer = setTimeout(() => {
        onUpgradeClick();
      }, 500);

      return () => clearTimeout(timer);
    }

    // Reset flag if user gains access
    if (hasAccess) {
      hasShownPaywallRef.current = false;
    }
  }, [hasAccess, onUpgradeClick]);

  const getTodaysDate = () => {
    return new Date().toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getDaysSinceLastPrayer = () => {
    if (!rosaryStreak.lastPrayerDate) return null;
    const lastDate = new Date(rosaryStreak.lastPrayerDate);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - lastDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStreakStatus = () => {
    const daysSince = getDaysSinceLastPrayer();
    if (!daysSince) return { text: 'Start your prayer journey', color: 'text-gray-500' };
    if (daysSince === 0) return { text: 'Prayed today!', color: 'text-green-600' };
    if (daysSince === 1) return { text: 'Prayed yesterday', color: 'text-blue-600' };
    return { text: `${daysSince} days ago`, color: 'text-orange-600' };
  };

  const streakStatus = getStreakStatus();

  // Calculate history statistics
  const getHistoryStats = () => {
    const completedSessions = rosaryStreak.sessions.filter(s => s.completed);
    const recentSessions = completedSessions
      .filter(s => {
        const sessionDate = new Date(s.date);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return sessionDate >= thirtyDaysAgo;
      })
      .sort((a, b) => b.date.localeCompare(a.date));

    const withReflections = completedSessions.filter(s => s.reflection || s.insights || (s.gratitudes && s.gratitudes.length > 0)).length;
    const reflectionPercentage = completedSessions.length > 0 ? Math.round((withReflections / completedSessions.length) * 100) : 0;

    return {
      totalCompleted: completedSessions.length,
      recent30Days: recentSessions.length,
      reflectionPercentage,
      recentSessions: recentSessions.slice(0, 3) // Show last 3 sessions
    };
  };

  const historyStats = getHistoryStats();

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Unified Header */}
      <UnifiedHeader
        onShowSettings={onShowSettings}
        onOpenSearch={onOpenSearch}
        subtitle={
          <div>
            <p className="text-base sm:text-lg font-light tracking-wide text-gray-600 dark:text-gray-400 mb-2">
              <span className="hidden sm:inline">Rosary & Devotion Tracker</span>
              <span className="sm:hidden">Prayer Tracker</span>
            </p>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">{getTodaysDate()}</p>
          </div>
        }
      />

      {/* Trial Status Banner */}
      <TrialBanner onUpgradeClick={onUpgradeClick} />

      {/* Offline Grace Period Warning */}
      <OfflineGraceWarning />

      {/* Sacred Art Viewer */}
      <SacredArtViewer />

      {/* 54-Day Novena Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl hover:shadow-2xl overflow-hidden transition-all duration-300">
        <div className="bg-gradient-to-r from-sacred-navy to-sacred-navy-deep p-4 border-b-2 border-sacred-gold-muted">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-white" />
              <h2 className="text-xl font-semibold text-white">54-Day Novena to Our Lady</h2>
            </div>
            {onShow54DayNovenaInfo && (
              <button
                onClick={onShow54DayNovenaInfo}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                title="Learn about the 54-Day Novena"
              >
                <Info className="w-5 h-5 text-white" />
              </button>
            )}
          </div>
        </div>
        
        <div className="p-4 sm:p-6">
          {isNovenaActive ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {completedDays.size}/{TOTAL_DAYS} days
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {Math.round(completionPercentage)}% complete
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-medium text-gray-800 dark:text-white">
                    Day {currentDay}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {currentDay <= 27 ? 'Petition Phase' : 'Thanksgiving Phase'}
                  </p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 mb-4 transition-colors duration-300">
                <div 
                  className="bg-gradient-to-r from-sacred-gold to-sacred-gold-light h-3 rounded-full transition-all duration-300"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>

              {isNovenaComplete ? (
                <div className="text-center py-4">
                  <p className="text-green-600 dark:text-green-400 font-medium mb-2">
                    🎉 Novena Complete! Congratulations!
                  </p>
                  <button
                    onClick={onStartNovena}
                    className="bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                  >
                    Start New Novena
                  </button>
                </div>
              ) : (
                <button
                  onClick={hasAccess ? onContinueNovena : onUpgradeClick}
                  className="w-full bg-gradient-to-r from-sacred-navy to-sacred-navy-deep hover:from-sacred-navy-light hover:to-sacred-navy text-white py-3 sm:py-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 min-h-[48px]"
                >
                  <Heart className="w-5 h-5" />
                  Continue Novena - Day {currentDay}
                </button>
              )}
            </>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Begin the traditional 54-day novena devotion to Our Lady of the Rosary. 
                27 days of petition followed by 27 days of thanksgiving.
              </p>
              <button
                onClick={hasAccess ? onStartNovena : onUpgradeClick}
                className="w-full bg-gradient-to-r from-sacred-navy to-sacred-navy-deep hover:from-sacred-navy-light hover:to-sacred-navy text-white py-3 sm:py-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 min-h-[48px]"
              >
                <Calendar className="w-5 h-5" />
                Start 54-Day Novena
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Daily Rosary Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl hover:shadow-2xl overflow-hidden transition-all duration-300">
        <div className="bg-gradient-to-r from-rose-400 to-rose-600 p-4 border-b-2 border-sacred-gold-muted">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-white" />
              <h2 className="text-xl font-semibold text-white">Daily Rosary</h2>
            </div>
            {onShowRosaryInfo && (
              <button
                onClick={onShowRosaryInfo}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                title="Learn about the Daily Rosary"
              >
                <Info className="w-5 h-5 text-white" />
              </button>
            )}
          </div>
        </div>
        
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">
                    {rosaryStreak.currentStreak}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">day streak</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                <div>
                  <p className="text-lg font-medium text-gray-800 dark:text-white">
                    {rosaryStreak.longestStreak}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">best streak</p>
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-medium text-gray-800 dark:text-white">
                {rosaryStreak.totalPrayers}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">total prayers</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-gray-400" />
            <p className={`text-sm ${streakStatus.color} dark:${streakStatus.color.replace('text-', 'text-')}`}>
              {streakStatus.text}
            </p>
          </div>

          <button
            onClick={hasAccess ? onPrayRosary : onUpgradeClick}
            className="w-full bg-gradient-to-r from-rose-400 to-rose-600 hover:from-rose-500 hover:to-rose-700 text-white py-3 sm:py-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 min-h-[48px]"
          >
            <BookOpen className="w-5 h-5" />
            Pray Today's Rosary
          </button>
        </div>
      </div>

      {/* Novenas Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl hover:shadow-2xl overflow-hidden transition-all duration-300">
        <div className="bg-gradient-to-r from-blue-400 to-blue-600 p-4 border-b-2 border-sacred-gold-muted">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-white" />
              <h2 className="text-xl font-semibold text-white">Novenas</h2>
            </div>
            <button
              onClick={onShowNovenaInfo}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              title="Learn about Novenas"
            >
              <Info className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <MyNovenas
            activeNovenas={activeNovenas}
            onContinueNovena={hasAccess ? onContinueIndividualNovena : () => onUpgradeClick()}
            onStartNewNovena={hasAccess ? onStartNewNovena : onUpgradeClick}
            onRemoveNovena={onRemoveNovena}
          />
        </div>
      </div>

      {/* Liturgical Recommendations */}
      {hasAccess && (onStartRecommendedNovena && onStart54DayFromRecommendation) && (
        <RecommendationsSection
          activeNovenas={activeNovenas.map(n => n.type)}
          has54DayActive={!!novenaState.startDate}
          onStartNovena={onStartRecommendedNovena}
          onStart54DayNovena={onStart54DayFromRecommendation}
        />
      )}

      {/* Traditional Chaplets Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl hover:shadow-2xl overflow-hidden transition-all duration-300">
        <div className="bg-sacred-parchment-dark dark:bg-sacred-stone-dark p-4 border-b-2 border-sacred-gold-muted">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-sacred-gold-dark dark:text-sacred-gold-light" />
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Traditional Chaplets</h2>
            </div>
            {onShowChapletInfo && (
              <button
                onClick={onShowChapletInfo}
                className="p-2 hover:bg-sacred-gold hover:bg-opacity-20 rounded-full transition-colors"
                title="Learn about Traditional Chaplets"
              >
                <Info className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </button>
            )}
          </div>
        </div>
        
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-6">
            {displayFavorites.map((chapletKey) => {
              const chaplet = CHAPLET_INFO[chapletKey];
              return (
                <button
                  key={chapletKey}
                  onClick={() => hasAccess ? (onSelectChaplet ? onSelectChaplet(chapletKey) : onPrayChaplet()) : onUpgradeClick()}
                  className="bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg p-4 text-center transition-all duration-200 group"
                >
                  <div className="text-2xl mb-1 transition-transform duration-200">
                    {chaplet.icon}
                  </div>
                  <div className="text-xs font-medium text-gray-800 dark:text-white mb-1 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                    {chaplet.name.replace('Chaplet of ', '').replace('Chaplet ', '')}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    ~{chaplet.estimatedDuration} min
                  </div>
                </button>
              );
            })}
          </div>

          <div className="text-center mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {displayFavorites.map(chapletKey =>
                CHAPLET_INFO[chapletKey].name.replace('Chaplet of ', '').replace('Chaplet ', '')
              ).join(' • ')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              ❤️ Your favorite chaplets - tap to pray directly
            </p>
          </div>

          <button
            onClick={hasAccess ? onPrayChaplet : onUpgradeClick}
            className="w-full bg-sacred-gold-muted hover:bg-sacred-gold text-gray-800 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 min-h-[48px]"
          >
            <Sparkles className="w-5 h-5" />
            Choose from All Chaplets
          </button>
        </div>
      </div>

      {/* Prayer History Card */}
      {historyStats.totalCompleted > 0 && onShowHistory && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl hover:shadow-2xl overflow-hidden transition-all duration-300">
          <div className="bg-gradient-to-r from-teal-500 to-teal-700 p-4 border-b-2 border-sacred-gold-muted">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <History className="w-6 h-6 text-white" />
                <h2 className="text-xl font-semibold text-white">Prayer History & Journal</h2>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                  {historyStats.totalCompleted}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  completed prayers
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                  {historyStats.recent30Days}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  last 30 days
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {historyStats.reflectionPercentage}%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  with reflections
                </div>
              </div>
            </div>

            {/* Recent Sessions Preview */}
            {historyStats.recentSessions.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Recent Prayers
                </h3>
                <div className="space-y-2">
                  {historyStats.recentSessions.map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transition-colors duration-300"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                        <div>
                          <div className="text-sm font-medium text-gray-800 dark:text-white">
                            {getPrayerTypeDisplayName(session)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(session.date).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        {session.intention && (
                          <div className="flex items-center gap-1">
                            <Heart className="w-3 h-3 text-red-400" />
                            <span>Intention</span>
                          </div>
                        )}
                        {session.reflection && (
                          <div className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3 text-blue-400" />
                            <span>Reflection</span>
                          </div>
                        )}
                        {session.tags && session.tags.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Tag className="w-3 h-3 text-purple-400" />
                            <span>{session.tags.length}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={onShowHistory}
              className="w-full bg-sacred-green-muted hover:bg-sacred-blue-muted text-white py-3 sm:py-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 min-h-[48px]"
            >
              <History className="w-5 h-5" />
              View Full Prayer History
            </button>
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
};